import * as yup from "yup";

import { Alert, AlertTitle, Divider } from "@mui/material";
import {
	EnhancedDropDownRHFWithLabel,
	EnhancedSnackbar,
	IResponse,
	IServerError,
	LabelPosition,
	RHookFormTextFieldWithLabel,
	StandardPanel,
	StyledRouterLink,
	SubmitButton,
	SubmitButtonVariant,
} from "@websential/cosmic-v20";
import { FormProvider, useForm } from "react-hook-form";
import { INITIAL_SNACKBAR_DATA, timeZoneItems } from "../../common/constants";
import { IRegister, IRegisterResponse } from "./IRegister";
import {
	LabelCaption,
	PlaceholderText,
	ValidationMessages,
} from "../../common/AppMessages";
import { consoleValue, roleTypeForUserDDL } from "../../common/Common";

import { AccountFormLayout } from "../AccountFormLayout";
import { AuthService } from "../../services/AuthService";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import Grid from "@mui/material/Grid";
import { IObject } from "../../models/common/IObject";
import { InputSwitch } from "primereact/inputswitch";
import TermsAndConditions from "./TermsAndConditions";
import Typography from "@mui/material/Typography";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";

export interface ICreateAnAccountForm {
	firstName: string;
	lastName: string;
	email: string;
	isSuperStatus: number;
	timezone?: IObject;
	primaryUser?: boolean;
	companyAccountantName?: string;
}
export interface ICreateAnUserForm {
	id: string | undefined;
	navigateToLogout: (path: any) => void,
	firstName: string;
	lastName: string;
	email: string;
	// isSuperStatus: number;
	// primaryUser: boolean;
	timezone?: IObject;
	// createdByExternalId: string;
	adminAccountantUser: boolean;
}



const createAnAccountFormSchema = yup.object({
	firstName: yup.string().max(50).required(ValidationMessages.firstName),
	lastName: yup.string().max(50).required(ValidationMessages.lastName),
	companyAccountantName: yup.string().max(50).required(ValidationMessages.companyAccountantName),
	isSuperStatus: yup.number().required(ValidationMessages.isSuperStatus),
	// email: yup
	// 	.string()
	// 	.email(ValidationMessages.invalidEmailFormat)
	// 	.max(50)
	// 	.required(ValidationMessages.email),
	email: yup.string()
		.trim()
		.required("Email is required")
		.email("Must be a valid email address")
		// .matches(/\.[A-Za-z]{2,}$/, "Must be a valid email address")
				.matches(/\.[A-Za-z]{2,}$/, {
			message: "Must be a valid email address",
			excludeEmptyString: true,
		})
		.max(50, "Maximum length 50 characters"),
	timezone: yup.string().required(),
	terms: yup.boolean().test({
		name: "terms",
		message: "Please accept the terms!",
		test: (value) => value === true,
	}),
});

const AlreadyHaveAnAccountLinkComponent = () => {
	return (
		<Grid
			size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
			style={{ marginTop: "1.3rem" }}
		>
			<Typography variant="subtitle2">
				Already have an account?&nbsp;
				<StyledRouterLink to="/login">Sign In</StyledRouterLink>
			</Typography>
		</Grid>
	);
};

const AccountRegisteredSuccessfully = () => {
	return (
		<StandardPanel title={`Welcome to SimplePay`} testId={`welcome-panel`}>
			<Alert severity="success" sx={{ marginBottom: "5px" }}>
				Congratulations! Your registration is now complete.
			</Alert>
			<Alert severity="warning" sx={{ marginBottom: "10px" }}>
				Your account activation link has been sent to your email address.
			</Alert>
			<Divider />
			<Typography
				variant="subtitle2"
				sx={{ paddingTop: "10px", textAlign: "right" }}
			>
				Already have an account?&nbsp;
				<StyledRouterLink to="/login">Sign In</StyledRouterLink>
			</Typography>
		</StandardPanel>
	);
};
interface IAccountProps {
	successPanel: (value: string) => void;
}

const CreateAnAccountFormComponent: React.FC<IAccountProps> = ({
	successPanel,
}) => {
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const [visibleTermsAndConditions, setVisibleTermsAndConditions] =
		useState(false);
	const methods: any = useForm<any>({
		resolver: yupResolver(createAnAccountFormSchema),
	});

	const { isPending: isSubmitting, mutate: registerUser } = useMutation({
		mutationFn: AuthService.register,

		onSuccess: (
			res:
				| IRegisterResponse
				| IServerError
				| any
				| IResponse
				| ICreateAnAccountForm
		) => {
			if (res.message === "User register successfully.") {
				successPanel(
					"Your registration is now complete. Your account activation link has been sent to your email address."
				);
			} else {

				setSnackbarData({
					open: true,
					message: res?.message ? res.message : "Somethingwent wrong!",
					severity: res.status === false ? "error" : "success",
				});
			}
		},
		onError: (err) => {
			// TODO: customize the toast message based on the response
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	});

	const onSnackbarClosed = (
		_event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};
	const onSelectionChange = async (e: any) => {
		alert(e);
	};
	const [checked, setChecked] = useState(false);

	const onSubmit = (data: ICreateAnAccountForm) => registerUser({ ...data, primaryUser: true });
	consoleValue(methods.formState.isDirty); // make sure formState is read before render to enable the Proxy

	const footerContent = (
		<div>
			<Button
				label="Ok"
				icon="pi pi-check"
				onClick={() => {
					setVisibleTermsAndConditions(false);
				}}
				size="small"
			/>
		</div>
	);

	const [value, setValue] = useState(false);
	const onSelectionChangeHandler = (event: any) => {
		setValue(event.target.value);
		methods.setValue("terms", event.target.value);
	};

	const [companyAccountantLabel, setCompanyAccountantLabel] = useState("Company");
	const onAccountTypeChangeHandler = (event: any) => {
		setCompanyAccountantLabel(event.value === 200 ? "Company" : "Accountant");
	};
	return (
		<>
			<FormProvider {...methods}>
				<form onSubmit={methods.handleSubmit(onSubmit)}>
					<Grid container size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
						<Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
							<RHookFormTextFieldWithLabel
								label={LabelCaption.firstName}
								id="firstName"
								testId="firstName"
								placeholder={PlaceholderText.firstName}
							/>
						</Grid>
						<Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
							<RHookFormTextFieldWithLabel
								label={LabelCaption.lastName}
								id="lastName"
								testId="lastName"
								placeholder={PlaceholderText.lastName}
							/>
						</Grid>
						<Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
							<RHookFormTextFieldWithLabel
								label={LabelCaption.email}
								id="email"
								testId="email"
								placeholder={PlaceholderText.email}
							/>
						</Grid>
						<Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
							<EnhancedDropDownRHFWithLabel
								label="Account Type"
								selectName="isSuperStatus"
								testId={`isSuperStatus`}
								labelPosition={LabelPosition.Top}
								items={roleTypeForUserDDL}
								onSelectionChangeHandler={onAccountTypeChangeHandler}
							/>
						</Grid>
						<Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
							<RHookFormTextFieldWithLabel
								label={companyAccountantLabel}
								id="companyAccountantName"
								testId={`companyAccountantName`}
								labelPosition={LabelPosition.Top}
							/>
						</Grid>
						<Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
							{/* <RHookFormTextFieldWithLabel
              label={LabelCaption.timezone}
              id="timezone"
              testId="timezone"
              placeholder={PlaceholderText.timezone}
              required={true}
            /> */}
							<EnhancedDropDownRHFWithLabel
								selectName="timezone"
								label={LabelCaption.timezone}
								// selectedItem={selectedProvince.id !== null && selectedProvince}
								labelPosition={LabelPosition.Top}
								testId={`time-zone-items-list`}
								items={timeZoneItems}
								selectedItem={"(GMT-05:00) Eastern Time (US & Canada)"}
							// onSelectionChangeHandler={onProvinceSelectionChangeHandler}
							/>
						</Grid>
					</Grid>
					<div className="p-field">
						<Typography
							sx={{
								paddingBottom: "8px",
								cursor: "pointer",
								"&.MuiTypography-root:hover": { color: "#066BB3" },
								color: "#018ef3",
								fontSize: "0.875rem",
							}}
							variant="body1"
							onClick={() => setVisibleTermsAndConditions(true)}
						>
							Terms & Conditions{" "}
							{/* <span style={{ color: "#d50000", fontSize: "1rem" }}>*</span> */}
						</Typography>
						<InputSwitch
							id="terms"
							checked={value}
							{...methods.register("terms")}
							className={methods.formState.errors.terms ? "p-invalid" : ""}
							onChange={onSelectionChangeHandler}
						/>
						{methods.formState.errors.terms && (
							<p
								className="p-error"
								style={{ fontSize: "0.75rem", marginTop: "3px" }}
							>
								{methods.formState.errors.terms.message}
							</p>
						)}
					</div>
					<SubmitButton
						label="Create an Account"
						variant={SubmitButtonVariant.Primary}
						loading={isSubmitting}
						testId="create-an-account-button"
					/>

					{/* Terms and Conditions Dialog BOX */}
					<Dialog
						header="Terms & Conditions"
						footer={footerContent}
						visible={visibleTermsAndConditions}
						style={{ width: "50vw" }}
						onHide={() => setVisibleTermsAndConditions(false)}
					>
						<TermsAndConditions />
					</Dialog>
					<AlreadyHaveAnAccountLinkComponent />
				</form>
			</FormProvider>
			<EnhancedSnackbar
				message={snackbarData.message}
				onCloseHandler={onSnackbarClosed}
				severity={snackbarData.severity}
				testId={"register-snackbar"}
				open={snackbarData.open}
			/>
		</>
	);
};

export const CreateAnAccountForm = () => {
	const [successResponse, setSuccessResponse] = useState<any>(undefined);
	return (
		<>
			{successResponse ? (
				<Grid container size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
					<Grid size={{ md: 1, lg: 1, xl: 1 }}></Grid>
					<Grid size={{ md: 10, lg: 10, xl: 10 }}>
						<AccountRegisteredSuccessfully />
					</Grid>
					<Grid size={{ md: 1, lg: 1, xl: 1 }}></Grid>
				</Grid>
			) : (
				<AccountFormLayout
					title="Create an Account"
					subtitle="Enter your details to create your account"
					form={
						<CreateAnAccountFormComponent
							successPanel={(value: any) => setSuccessResponse(value)}
						/>
					}
				/>
			)}
		</>
	);
};
